import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bug, X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FeedbackButton() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/feedback/report`, {
        message: message.trim(),
        page_url: window.location.href,
        user_agent: navigator.userAgent
      }, { withCredentials: true });

      toast.success(t('feedback.successMessage'));
      setMessage('');
      setShowModal(false);
    } catch (error) {
      console.error('Error sending feedback:', error);
      toast.error(t('feedback.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setShowModal(true)}
        data-testid="feedback-btn"
        className="fixed bottom-4 right-4 z-40 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-[#FF8BA7] hover:bg-[#ff7a9a] p-3 shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95"
        title={t('feedback.reportBug')}
      >
        <Bug className="w-5 h-5 text-[#18181B]" />
      </button>

      {/* Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white dark:bg-zinc-800 border-t-2 sm:border-2 border-[#18181B] dark:border-zinc-600 rounded-t-2xl sm:rounded-2xl hard-shadow p-4 sm:p-6 w-full sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="feedback-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#FF8BA7] border-2 border-[#18181B] dark:border-zinc-600 flex items-center justify-center">
                  <Bug className="w-5 h-5 text-[#18181B]" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-white">
                  {t('feedback.title')}
                </h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-900 dark:text-white" />
              </button>
            </div>

            {/* Description */}
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">
              {t('feedback.description')}
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-xs sm:text-sm font-bold mb-2 text-zinc-900 dark:text-white">
                  {t('feedback.messageLabel')}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('feedback.messagePlaceholder')}
                  rows={5}
                  className="w-full rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow resize-none"
                  data-testid="feedback-message-input"
                  autoFocus
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-3 px-4 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="feedback-cancel-btn"
                >
                  {t('modals.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!message.trim() || loading}
                  className="flex-1 rounded-full border-2 border-[#18181B] bg-[#FF8BA7] text-[#18181B] font-bold py-3 px-4 text-sm hover:bg-[#ff7a9a] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="feedback-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {t('feedback.send')}
                </button>
              </div>
            </form>

            {/* Footer note */}
            <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-4 text-center">
              {t('feedback.emailNote')}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
